import React from 'react';
import { InvoiceStats } from 'shared';

interface InvoiceStatsSummaryProps {
  stats: InvoiceStats | null;
  loading: boolean;
}

interface StatItem {
  label: string;
  count: number;
  amount: number;
  icon: string;
  gradient: string;
  dotColor: string;
}

export const InvoiceStatsSummary: React.FC<InvoiceStatsSummaryProps> = ({ stats, loading }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const items: StatItem[] = [
    { label: 'Total', count: stats?.total || 0, amount: stats?.totalAmount || 0, icon: 'pi-file', gradient: 'from-blue-500 to-blue-600', dotColor: 'bg-blue-500' },
    { label: 'Draft', count: stats?.draft || 0, amount: stats?.draftAmount || 0, icon: 'pi-pencil', gradient: 'from-orange-500 to-orange-600', dotColor: 'bg-orange-500' },
    { label: 'Approved', count: stats?.approved || 0, amount: stats?.approvedAmount || 0, icon: 'pi-check-circle', gradient: 'from-purple-500 to-purple-600', dotColor: 'bg-purple-500' },
    { label: 'Sent', count: stats?.sent || 0, amount: stats?.sentAmount || 0, icon: 'pi-send', gradient: 'from-green-500 to-green-600', dotColor: 'bg-green-500' },
    { label: 'Paid', count: stats?.paid || 0, amount: stats?.paidAmount || 0, icon: 'pi-check-circle', gradient: 'from-teal-500 to-teal-600', dotColor: 'bg-teal-500' },
    { label: 'Overdue', count: stats?.overdue || 0, amount: stats?.overdueAmount || 0, icon: 'pi-exclamation-triangle', gradient: 'from-orange-500 to-red-500', dotColor: 'bg-red-500' },
  ];

  return (
    <div data-component="invoice-stats-summary" className="mb-4">
      <div className="grid grid-cols-3 gap-1 sm:gap-2 sm:grid-cols-6">
        {items.map((item) => (
          <div
            key={item.label}
            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 sm:px-3 sm:py-2.5 shadow-sm"
          >
            <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
              <span className={`inline-block w-1.5 h-1.5 sm:hidden rounded-full ${item.dotColor}`} />
              <div className={`hidden sm:flex items-center justify-center w-5 h-5 rounded bg-gradient-to-r ${item.gradient}`}>
                <i className={`pi ${item.icon} text-white`} style={{ fontSize: '0.65rem' }} />
              </div>
              <span className="text-[0.6rem] sm:text-xs font-medium text-gray-500 truncate">{item.label}</span>
            </div>
            <p className="text-sm sm:text-xl font-bold text-gray-900 leading-tight">
              {loading ? '-' : item.count}
            </p>
            <p className="text-[0.6rem] sm:text-xs text-gray-400 mt-0.5">
              {loading ? '-' : formatCurrency(item.amount)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
